// src/components/ProjectModal.js
import React, { useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import MultipleFileUpload from './MultipleFileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';

// 없어도 실사용에 문제는 없지만 개발자모드에서 에러경고 나오기에 설정
// isNumericString prop을 필터링하는 커스텀 Input 컴포넌트
const FilteredInput = React.forwardRef((props, ref) => {
  const { isNumericString, ...rest } = props; // isNumericString 제거
  return <input ref={ref} {...rest} />;
});

// 숫자 입력에 천 단위 구분자를 적용하기 위한 커스텀 컴포넌트
function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      // 값이 변경될 때 숫자 값으로 변환해서 전달
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value === "" ? "" : Number(values.value),
          },
        });
      }}
      thousandSeparator
      isNumericString={true}  // boolean prop을 DOM에 전달하지 않고 내부에서 처리 완료했다고 명시
      customInput={FilteredInput} // 커스텀 Input 사용: isNumericString이 제거됨
    />
  );
}


const ProjectModal = ({open, handleClose, onSubmit, defaultValues, onProjectDeleted /*삭제 후 상위로 알리는 콜백*/ }) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      siteName: '',
      manager: '',
      constructionDetails: '',
      constructionStatus: '',
      constructionPre: '',
      remarks: '',
      estimateAmount: '',
      materialCost: '',
      outsourcingCost: '',
      outsourcingCompany: '',
      // difference: 0,
      startDate: '',
      completeDate: '',
      ipList: '',
      accountInfo: '',
      drawings: [],
      photos: [],
      contractFiles: [],
      latestUpdate: '',
      xref: ''
      //percentage: 0
    }
  });

  // defaultValues가 변경되면 폼 값을 reset
  useEffect(() => {
    if(open){
      if (defaultValues) {
        reset({
          ...defaultValues,
          // DB에서 받아온 파일 배열이 있다면 그대로 사용, 없으면 빈 배열
          drawings: defaultValues.drawings || [],
          drawingsToDelete: [],
          photos: defaultValues.photos || [],
          photosToDelete: [],
          contractFiles: defaultValues.contractFiles || [],
          contractFilesToDelete: [],
      });
    }
    } else {
      reset({
        siteName: '',
        manager: '',
        constructionDetails: '',
        constructionStatus: '',
        constructionPre: '',
        remarks: '',
        estimateAmount: '',
        materialCost: '',
        outsourcingCost: '',
        outsourcingCompany: '',
        //difference: 0,
        uploadDate: '',
        startDate: '',
        completeDate: '',
        ipList: '',
        accountInfo: '',
        drawings: [],
        drawingsToDelete: [],
        photos: [],
        photosToDelete: [],
        contractFiles: [],
        contractFilesToDelete: [],
        latestUpdate: '',
        xref: '',
        //percentage: 0
      });
    }
  }, [defaultValues, open, reset]);
  
  // // DB에 차액 값이 없으면 (undefined 또는 null) 계산해서 넣어줍니다.
  // useEffect(() => {
  //     const computedDifference = Number(estimateAmount) - Number(materialCost) - Number(outsourcingCost);
  //     setValue("difference", computedDifference);
  // }, [estimateAmount, materialCost, outsourcingCost, defaultValues, setValue]);

  // // DB에 백분율 값이 없으면 (undefined 또는 null) 차액과 견적금액을 이용해 계산(소수점 한 자리)하여 넣어줍니다.
  // useEffect(() => {
    
  //     if (Number(estimateAmount) !== 0) {
  //       const computedPercentage = ((Number(difference) / Number(estimateAmount)) * 100).toFixed(1);
  //       setValue("percentage", computedPercentage);
  //     } else {
  //       setValue("percentage", 0);
  //     }
  // }, [difference, estimateAmount, defaultValues, setValue]);

  console.log(defaultValues?.xref);

  // 프로젝트 삭제 요청 처리 (삭제 버튼 클릭 시 호출)
  const handleDeleteProject = async () => {
    if (!defaultValues || !defaultValues.id) return;
    if (!window.confirm("정말 이 공사를 삭제하시겠습니까?")) return;
    try {
      const response = await fetch(`/api/projects/${defaultValues.id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error("삭제 실패");
      }
      // 삭제 성공 시 모달 닫기
      handleClose();
      if (onProjectDeleted) onProjectDeleted(defaultValues.id);
    } catch (error) {
      console.error("프로젝트 삭제 오류:", error);
    }
  };

  const submitHandler = async (data) => {
    // onSubmit을 반드시 await 해주어야 RHF가 isSubmitting을 true로 유지합니다.
    await onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pt: 3 }}>공사 등록</DialogTitle>
      <DialogContent>
        <form id="project-form" onSubmit={handleSubmit(submitHandler)}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {defaultValues?.xref && (() => {
              const [_, bo, id] = defaultValues.xref.split('|'); // "gnb|bo_table|wr_id"
              const gnbUrl = `https://intra.hskt.kr/bbs/board.php?bo_table=${bo}&wr_id=${id}`;
              return(
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    <a href={gnbUrl} target="_blank" rel="noopener noreferrer">일정관리 게시글 바로가기</a>
                  </Typography>
                </Grid>
              )
            })()}
            {/* 공사 상태 선택 (공사 등록 바로 아래에 위치) */}
            <Grid item xs={12}>
              <Controller
                name="constructionPre"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="construction-state-label">공사상태</InputLabel>
                    <Select
                      {...field}
                      labelId="construction-state-label"
                      label="공사 상태"
                      sx={{
                        backgroundColor:
                          field.value === "공사중"
                            ? "lightgreen"
                            : field.value === "공사보류"
                            ? "lightgray"
                            : field.value === "공사완료"
                            ? "lightblue"
                            : "inherit"
                      }}
                    >
                      <MenuItem value="공사준비">공사준비</MenuItem>
                      <MenuItem value="공사중">공사중</MenuItem>
                      <MenuItem value="공사보류">공사보류</MenuItem>
                      <MenuItem value="공사완료">공사완료</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            {/* 공사내용 입력 */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="siteName"
                control={control}
                rules={{ required: '현장명을 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="현장명"
                    fullWidth
                    required
                    error={!!errors.siteName}
                    helperText={errors.siteName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="manager"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="담당자"
                    fullWidth
                    error={!!errors.manager}
                    helperText={errors.manager?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="constructionDetails"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="공사명"
                    multiline
                    rows={2} // 필요한 경우 행 수 조정 가능
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="constructionStatus"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="공사현황"
                    multiline
                    rows={5}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="비고"
                    multiline
                    rows={2}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="estimateAmount"
                control={control}
                rules={{ 
                  required: '견적금액을 입력하세요',
                  min: { value: 1000000, message: '최소 100만 이상이어야 합니다' }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    label="견적금액" 
                    fullWidth 
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="materialCost"
                control={control}
                render={({ field }) => (
                  <TextField 
                  {...field} 
                    label="자재비" 
                    fullWidth 
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="outsourcingCost"
                control={control}
                render={({ field }) => (
                  <TextField 
                  {...field} 
                    label="외주비" 
                    fullWidth 
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="outsourcingCompany"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="외주업체" fullWidth />
                )}
              />
            </Grid>
            {/* <Grid item xs={12} sm={3}>
              <Controller
                name="difference"
                control={control}
                render={({ field }) => (
                  <TextField 
                  {...field} 
                    label="차액" 
                    fullWidth 
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled
                  />
                )}
              />
            </Grid> */}
            <Grid item xs={12} sm={4}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="착공예정일"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="completeDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="준공일"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            {/* 상세내용 입력 */}
            <Grid item xs={12}>
              <Controller
                name="ipList"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="IP 리스트"
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="accountInfo"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="계정정보" fullWidth />
                )}
              />
            </Grid>
            {/* 파일 첨부 - MultipleFileUpload 컴포넌트 사용 */}
            <Grid item xs={12}>
              <MultipleFileUpload
                label="도면 및 IP, 계정정보"
                initialFiles={defaultValues?.drawings || []}
                onFilesChange={(files, filesToDelete) => {
                  setValue('drawings', files);
                  setValue('drawingsToDelete', filesToDelete);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MultipleFileUpload
                label="사진대지"
                initialFiles={defaultValues?.photos || []}
                onFilesChange={(files, filesToDelete) => {
                  setValue('photos', files);
                  setValue('photosToDelete', filesToDelete);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MultipleFileUpload
                label="내역서"
                initialFiles={defaultValues?.contractFiles || []}
                onFilesChange={(files, filesToDelete) => {
                  setValue('contractFiles', files);
                  setValue('contractFilesToDelete', filesToDelete);
                }}
              />
            </Grid>
            {defaultValues?.latestUpdate && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  수정일자: {defaultValues.latestUpdate}
                </Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx = {{ justifyContent : 'space-between'}}>
        {/* 삭제 버튼: 업데이트 모드에서만 보임 */}
        {defaultValues && defaultValues.id && (
          <IconButton aria-label="delete" size="large" onClick={handleDeleteProject}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        )}
        <Box>
          <Button onClick={handleClose}>취소</Button>
          <Button variant="contained" type="submit" form="project-form" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : null}>
            {isSubmitting ? '저장중...' : '저장'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectModal;
